import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import IPButton from '../../../../../components/IPButton'
import IPCheckBox from '../../../../../components/IPCheckBox'
import IPSelectBox from '../../../../../components/IPSelectBox'
import InputCampaign from '../../../../../components/InputCampaign'
import {
  DEALER_TYPE_OPTIONS,
  DealerTypeFilter,
  DEFAULT_EXCLUDE_TEST_DEALERS,
  EXCLUDE_TEST_DEALERS_LABEL,
} from '../analyticsQuery'
import { DEFAULT_FEEDBACK_VOTE } from '../feedbackResolution'
import {
  FeedbackVoteFilter,
  IChatAnalyticsSummary,
  IChatDealerBreakdown,
  IChatDealerUsageReport,
  IChatUserUsageReport,
  IDealerUsageParams,
} from '../models'
import { GetFeedbackReportApi } from '../services/getFeedbackReportApi'
import { GetResolutionReportApi } from '../services/getResolutionReportApi'
import { ReopenResolutionApi } from '../services/reopenResolutionApi'
import { ResolveFeedbackApi } from '../services/resolveFeedbackApi'
import { GetQuestionReportApi } from '../services/getQuestionReportApi'
import { GetChatbotAnalyticsSummaryApi } from '../services/getChatbotAnalyticsSummaryApi'
import { GetDealerUsageApi } from '../services/getDealerUsageApi'
import { GetUserUsageApi } from '../services/getUserUsageApi'
import { GetDealerBreakdownApi } from '../services/getDealerBreakdownApi'
import {
  dateRangeError,
  endExclusiveOfLocalDay,
  fromDateInputValue,
  loadErrorMessage,
  MAX_RANGE_DAYS,
  startOfLocalDay,
  toDateInputValue,
} from '../viewModel'
import ChatbotAnalyticsContent from './ChatbotAnalyticsContent'
import DealerUsageTab from './DealerUsageTab'
import FeedbackReportTab, { FeedbackReportLoader } from './FeedbackReportTab'
import { ResolveFeedbackAction } from './FeedbackResolveModal'
import QuestionsTab, { QuestionReportLoader } from './QuestionsTab'
import ResolutionsTab, { ReopenResolutionAction, ResolutionReportLoader } from './ResolutionsTab'
import styles from './ChatbotAnalytics.module.scss'

type LoadState = 'loading' | 'ready' | 'empty' | 'error'
type AnalyticsTab = 'overview' | 'usage' | 'feedback' | 'questions' | 'resolutions'

interface DateRange {
  start: Date
  end: Date
}

export type ChatbotAnalyticsSummaryLoader = (
  fromUtc: Date,
  toUtc: Date,
  excludeTestDealers: boolean,
  dealerType?: DealerTypeFilter
) => Promise<IChatAnalyticsSummary>
export type DealerUsageLoader = (params: IDealerUsageParams) => Promise<IChatDealerUsageReport>
export type UserUsageLoader = (params: IDealerUsageParams) => Promise<IChatUserUsageReport>
export type DealerBreakdownLoader = (
  dealerSapCode: string,
  fromUtc: Date,
  toUtc: Date
) => Promise<IChatDealerBreakdown>

interface ChatbotAnalyticsProps {
  loadSummary?: ChatbotAnalyticsSummaryLoader
  loadDealerUsage?: DealerUsageLoader
  loadUserUsage?: UserUsageLoader
  loadBreakdown?: DealerBreakdownLoader
  /** Testte enjekte edilir; verilmezse admin API'si kullanılır. */
  loadFeedbackReport?: FeedbackReportLoader
  loadQuestionReport?: QuestionReportLoader
  loadResolutions?: ResolutionReportLoader
  resolveFeedback?: ResolveFeedbackAction
  reopenResolution?: ReopenResolutionAction
}

const loadSummaryFromApi: ChatbotAnalyticsSummaryLoader = (fromUtc, toUtc, excludeTestDealers, dealerType) => {
  const api = new GetChatbotAnalyticsSummaryApi({ screenCode: 'ChatbotAnalytics' })
  return api.getSummary(fromUtc, toUtc, excludeTestDealers, dealerType)
}

const loadDealerUsageFromApi: DealerUsageLoader = params => {
  const api = new GetDealerUsageApi({ screenCode: 'ChatbotAnalytics' })
  return api.getReport(params)
}

const loadUserUsageFromApi: UserUsageLoader = params => {
  const api = new GetUserUsageApi({ screenCode: 'ChatbotAnalytics' })
  return api.getReport(params)
}

const loadBreakdownFromApi: DealerBreakdownLoader = (dealerSapCode, fromUtc, toUtc) => {
  const api = new GetDealerBreakdownApi({ screenCode: 'ChatbotAnalytics' }, dealerSapCode)
  return api.getBreakdown(fromUtc, toUtc)
}

const loadFeedbackReportFromApi: FeedbackReportLoader = params => {
  const api = new GetFeedbackReportApi({ screenCode: 'ChatbotAnalytics' })
  return api.getReport(params)
}

const loadQuestionReportFromApi: QuestionReportLoader = params => {
  const api = new GetQuestionReportApi({ screenCode: 'ChatbotAnalytics' })
  return api.getReport(params)
}

const loadResolutionsFromApi: ResolutionReportLoader = params => {
  const api = new GetResolutionReportApi({ screenCode: 'ChatbotAnalytics' })
  return api.getReport(params)
}

const resolveFeedbackWithApi: ResolveFeedbackAction = (feedbackId, request) => {
  const api = new ResolveFeedbackApi({ screenCode: 'ChatbotAnalytics' }, feedbackId)
  return api.resolve(request)
}

const reopenResolutionWithApi: ReopenResolutionAction = resolutionId => {
  const api = new ReopenResolutionApi({ screenCode: 'ChatbotAnalytics' }, resolutionId)
  return api.reopen()
}

/** Varsayılan başlangıç: 05.10.2026. */
const DEFAULT_START_DATE = new Date(2026, 9, 5)

const SUMMARY_ERROR_TEXT = 'Chatbot analitik verileri yüklenemedi. Lütfen tekrar deneyin.'

/**
 * Başlangıç 05.10.2026, bitiş bugün. Aralık 90 gün sınırını aşacaksa başlangıç son 90 güne çekilir;
 * bugün o tarihten önceyse başlangıç bugündür.
 */
const createInitialDateRange = (): DateRange => {
  const end = startOfLocalDay(new Date())
  const earliest = new Date(end)
  earliest.setDate(earliest.getDate() - (MAX_RANGE_DAYS - 1))
  let start = DEFAULT_START_DATE < earliest ? earliest : new Date(DEFAULT_START_DATE)
  if (start > end) start = new Date(end)
  return { start, end }
}

/** Boş veya geçersiz değer taslağı DEĞİŞTİRMEZ (spec §4). */
const updateDraft = (value: string, setDraft: (value: string) => void) => {
  if (fromDateInputValue(value)) setDraft(value)
}

const tabs: { key: AnalyticsTab; label: string }[] = [
  { key: 'overview', label: 'Genel Bakış' },
  { key: 'usage', label: 'Bayi & Kullanıcı' },
  { key: 'feedback', label: 'Geri Bildirimler' },
  { key: 'questions', label: 'Sorular' },
  { key: 'resolutions', label: 'Tamamlananlar' },
]

export default function ChatbotAnalytics({
  loadSummary = loadSummaryFromApi,
  loadDealerUsage = loadDealerUsageFromApi,
  loadUserUsage = loadUserUsageFromApi,
  loadBreakdown = loadBreakdownFromApi,
  loadFeedbackReport = loadFeedbackReportFromApi,
  loadQuestionReport = loadQuestionReportFromApi,
  loadResolutions = loadResolutionsFromApi,
  resolveFeedback = resolveFeedbackWithApi,
  reopenResolution = reopenResolutionWithApi,
}: ChatbotAnalyticsProps) {
  const initialRangeRef = useRef<DateRange | null>(null)
  if (!initialRangeRef.current) initialRangeRef.current = createInitialDateRange()

  // Taslak `YYYY-MM-DD` string'idir (InputCampaign type='date'); Date'e yalnız Uygula'da çevrilir.
  const [draftStart, setDraftStart] = useState(() => toDateInputValue(initialRangeRef.current!.start))
  const [draftEnd, setDraftEnd] = useState(() => toDateInputValue(initialRangeRef.current!.end))
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [summary, setSummary] = useState<IChatAnalyticsSummary | null>(null)
  const [summaryError, setSummaryError] = useState(SUMMARY_ERROR_TEXT)
  // Uygula'da yakalanan geçersiz aralık (ör. 90 günden uzun); istek atılmaz, filtrenin altında gösterilir.
  const [rangeError, setRangeError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('overview')
  // "Deneme Kartı Hariç": varsayılan işaretli; değişince hemen uygulanır ve tüm sekmelere geçer (Faz-37 §1c).
  const [excludeTestDealers, setExcludeTestDealers] = useState(DEFAULT_EXCLUDE_TEST_DEALERS)
  // "Bayi Tipi" (Faz-41): boş = tüm bayiler. Tarih filtresinin yanındadır ve TÜM sekmelere geçer.
  const [dealerType, setDealerType] = useState<DealerTypeFilter | undefined>(undefined)
  // Memnuniyetteki sayıya tıklanınca Geri Bildirimler bu oyla açılır; sekmeye doğrudan geçişte `down`.
  const [feedbackVote, setFeedbackVote] = useState<FeedbackVoteFilter>(DEFAULT_FEEDBACK_VOTE)
  // Uygulanan aralık STATE'tir (ref değil): kullanım sekmesi bunu prop olarak alır,
  // ref değişimi yeniden render tetiklemezdi.
  const [appliedRange, setAppliedRange] = useState<DateRange>(initialRangeRef.current)
  const requestSequenceRef = useRef(0)
  const mountedRef = useRef(false)
  // Sekmelere verilen sınırlar KİMLİK olarak sabittir: taslak tarih yazılırken ana bileşen yeniden
  // çizilse de sekmeler gereksiz yere yeniden istek atmaz.
  const appliedFromUtc = useMemo(() => startOfLocalDay(appliedRange.start), [appliedRange])
  const appliedToUtc = useMemo(() => endExclusiveOfLocalDay(appliedRange.end), [appliedRange])

  const load = useCallback(
    async (range: DateRange, exclude: boolean, type?: DealerTypeFilter) => {
      const requestSequence = ++requestSequenceRef.current
      setLoadState('loading')

      try {
        const response = await loadSummary(
          startOfLocalDay(range.start),
          endExclusiveOfLocalDay(range.end),
          exclude,
          type
        )
        if (!mountedRef.current || requestSequence !== requestSequenceRef.current) return

        setSummary(response)
        setLoadState(response.Summary.TotalTurns === 0 ? 'empty' : 'ready')
      } catch (error) {
        if (!mountedRef.current || requestSequence !== requestSequenceRef.current) return

        setSummary(null)
        setSummaryError(loadErrorMessage(error, SUMMARY_ERROR_TEXT))
        setLoadState('error')
      }
    },
    [loadSummary]
  )

  useEffect(() => {
    mountedRef.current = true
    load(initialRangeRef.current!, DEFAULT_EXCLUDE_TEST_DEALERS, undefined)

    return () => {
      mountedRef.current = false
      requestSequenceRef.current += 1
    }
  }, [load])

  const applyDates = () => {
    const start = fromDateInputValue(draftStart)
    const end = fromDateInputValue(draftEnd)
    if (!start || !end) return
    const error = dateRangeError(start, end)
    setRangeError(error)
    if (error) return
    const nextRange = { start, end }
    setAppliedRange(nextRange)
    load(nextRange, excludeTestDealers, dealerType)
  }

  const changeExcludeTestDealers = (checked: boolean) => {
    setExcludeTestDealers(checked)
    load(appliedRange, checked, dealerType)
  }

  /** Bayi tipi "Uygula"yı beklemez: tarih filtresindeki onay kutusuyla aynı davranış. */
  const changeDealerType = (next?: DealerTypeFilter) => {
    setDealerType(next)
    load(appliedRange, excludeTestDealers, next)
  }

  /** Sonuçlandırma / yeniden açma sonrası özet (memnuniyet, olumsuz sayısı) arka planda yenilenir. */
  const refreshSummary = useCallback(
    () => load(appliedRange, excludeTestDealers, dealerType),
    [load, appliedRange, excludeTestDealers, dealerType]
  )

  const showVotes = useCallback((vote: FeedbackVoteFilter) => {
    setFeedbackVote(vote)
    setActiveTab('feedback')
  }, [])

  const selectTab = (tab: AnalyticsTab) => {
    if (tab === 'feedback' && activeTab !== 'feedback') setFeedbackVote(DEFAULT_FEEDBACK_VOTE)
    setActiveTab(tab)
  }

  return (
    <main className={styles.shell} aria-busy={loadState === 'loading'}>
      <section className={styles.filterPanel} aria-labelledby='chatbot-date-filter-title'>
        <div>
          <span className={styles.eyebrow}>Rapor aralığı</span>
          <p className={styles.filterTitle} id='chatbot-date-filter-title'>
            Tarih Filtresi
          </p>
          <p>Bitiş tarihi dahil olacak şekilde yerel gün aralığını seçin.</p>
        </div>
        <div className={styles.filterControls}>
          {/* Native tarih seçici DOM'da kırpılmaz; açılır takvimin panellerin arkasında kalma sorunu yoktur. */}
          <div className={styles.dateField}>
            <InputCampaign
              type='date'
              id='chatbot-analytics-start-date'
              name='chatbot-analytics-start-date'
              label='Başlangıç tarihi'
              value={draftStart}
              max={draftEnd}
              onChange={event => updateDraft(event.currentTarget.value, setDraftStart)}
              aria-label='Chatbot analitiği başlangıç tarihi'
              isActive
            />
          </div>
          <div className={styles.dateField}>
            <InputCampaign
              type='date'
              id='chatbot-analytics-end-date'
              name='chatbot-analytics-end-date'
              label='Bitiş tarihi'
              value={draftEnd}
              min={draftStart}
              max={toDateInputValue(new Date())}
              onChange={event => updateDraft(event.currentTarget.value, setDraftEnd)}
              aria-label='Chatbot analitiği bitiş tarihi'
              isActive
            />
          </div>
          {/* Bayi kodları ön yüzde TUTULMAZ: seçim gönderilir, Vodafone tanımı sunucudadır. */}
          <div className={styles.toolbarSelect}>
            <IPSelectBox
              id='chatbot-analytics-dealer-type'
              aria-label='Bayi tipi'
              options={DEALER_TYPE_OPTIONS}
              value={DEALER_TYPE_OPTIONS.find(option => option.value === (dealerType ?? '')) ?? DEALER_TYPE_OPTIONS[0]}
              onChangeSingle={option =>
                changeDealerType(option?.value ? (String(option.value) as DealerTypeFilter) : undefined)
              }
              menuPortalTarget={document.body}
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            />
          </div>
          <div className={styles.filterActions}>
            <IPButton text='Uygula' type='button' onClick={applyDates} className={styles.primaryButton} />
            <IPButton
              text='Yenile'
              type='button'
              onClick={() => load(appliedRange, excludeTestDealers, dealerType)}
              className={styles.secondaryButton}
            />
          </div>
          {/* Test bayisi kodları sunucu ayarıdır; ön yüz yalnız açık/kapalı gönderir. */}
          <div className={styles.testDealerToggle} title='Deneme kartı (test bayisi) kullanımını raporlardan çıkarır'>
            <IPCheckBox
              id='chatbot-analytics-exclude-test-dealers'
              label={EXCLUDE_TEST_DEALERS_LABEL}
              checked={excludeTestDealers}
              removeMarginBottom
              onCheckedChange={changeExcludeTestDealers}
            />
          </div>
        </div>
        {rangeError ? (
          <p className={styles.rangeError} role='alert' data-testid='range-error'>
            {rangeError}
          </p>
        ) : null}
      </section>

      {/* Tarih filtresi sekmelerin ÜSTÜNDE: sekme değişince aralık korunur. */}
      <ul className={styles.tabs} role='tablist'>
        {tabs.map(tab => (
          <li
            key={tab.key}
            data-testid={`tab-${tab.key}`}
            role='tab'
            aria-selected={activeTab === tab.key}
            className={activeTab === tab.key ? styles.tabActive : undefined}
            onClick={() => selectTab(tab.key)}
          >
            {tab.label}
          </li>
        ))}
      </ul>

      {activeTab === 'overview' ? (
        <>
          {loadState === 'loading' ? (
            <section className={styles.statePanel} role='status' aria-live='polite'>
              <span className={styles.loadingMark} aria-hidden='true' />
              <h1>Chatbot Analitiği</h1>
              <p>Chatbot analitik verileri yükleniyor.</p>
            </section>
          ) : null}

          {loadState === 'error' ? (
            <section className={`${styles.statePanel} ${styles.errorState}`} role='alert'>
              <span className={styles.stateIcon} aria-hidden='true'>
                !
              </span>
              <h1>Chatbot Analitiği</h1>
              <p>{summaryError}</p>
            </section>
          ) : null}

          {(loadState === 'ready' || loadState === 'empty') && summary ? (
            <ChatbotAnalyticsContent data={summary} onShowVotes={showVotes} />
          ) : null}
        </>
      ) : null}

      {/* Tembel yükleme: kullanım verisi ancak sekmeye geçilince istenir. */}
      {activeTab === 'usage' ? (
        <DealerUsageTab
          fromUtc={appliedFromUtc}
          toUtc={appliedToUtc}
          loadDealerUsage={loadDealerUsage}
          loadUserUsage={loadUserUsage}
          loadBreakdown={loadBreakdown}
          excludeTestDealers={excludeTestDealers}
          dealerType={dealerType}
        />
      ) : null}

      {/* Geri Bildirimler de tembel yüklenir; uygulanan aralık sekmeler arasında korunur. */}
      {activeTab === 'feedback' ? (
        <FeedbackReportTab
          fromUtc={appliedFromUtc}
          toUtc={appliedToUtc}
          loadFeedbackReport={loadFeedbackReport}
          excludeTestDealers={excludeTestDealers}
          dealerType={dealerType}
          initialVote={feedbackVote}
          resolveFeedback={resolveFeedback}
          onResolved={refreshSummary}
        />
      ) : null}

      {activeTab === 'questions' ? (
        <QuestionsTab
          fromUtc={appliedFromUtc}
          toUtc={appliedToUtc}
          loadQuestionReport={loadQuestionReport}
          excludeTestDealers={excludeTestDealers}
          dealerType={dealerType}
        />
      ) : null}

      {/* Tamamlananlar: tarih aralığı KAPATMA zamanına uygulanır. */}
      {activeTab === 'resolutions' ? (
        <ResolutionsTab
          fromUtc={appliedFromUtc}
          toUtc={appliedToUtc}
          excludeTestDealers={excludeTestDealers}
          dealerType={dealerType}
          loadResolutions={loadResolutions}
          reopenResolution={reopenResolution}
          onReopened={refreshSummary}
        />
      ) : null}
    </main>
  )
}
